import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, AlertCircle, Loader2 } from "lucide-react";
import { calendarIntegrationsApi } from "../../services/api";

type CallbackStatus = "loading" | "success" | "error";

const CalendarCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<CallbackStatus>("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const processedRef = useRef(false);

  useEffect(() => {
    // OAuth codes are single-use  -  guard against React StrictMode double-execution
    if (processedRef.current) return;
    processedRef.current = true;

    const processCallback = async () => {
      const code = searchParams.get("code");
      const stateParam = searchParams.get("state");
      const error = searchParams.get("error");

      // If the OAuth provider returned an error
      if (error) {
        const errorDesc =
          searchParams.get("error_description") || "Authorization was denied or failed.";
        setErrorMessage(errorDesc);
        setStatus("error");
        return;
      }

      // Parse provider from state param (base64-encoded JSON from backend)
      let provider = "";
      if (stateParam) {
        try {
          const parsed = JSON.parse(atob(stateParam));
          provider = parsed.provider || "";
        } catch {
          // state might not be base64 JSON -- try using it directly
          provider = stateParam;
        }
      }

      if (!code || !provider) {
        setErrorMessage(
          "Missing authorization code or provider information. Please try connecting your calendar again."
        );
        setStatus("error");
        return;
      }

      try {
        await calendarIntegrationsApi.callback(provider, code, stateParam || "");
        setStatus("success");
      } catch (err: any) {
        console.error("Calendar OAuth callback failed:", err);
        const message =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to connect your calendar. Please try again.";
        setErrorMessage(message);
        setStatus("error");
      }
    };

    processCallback();
  }, [searchParams]);

  // Auto-redirect on success after 2 seconds
  useEffect(() => {
    if (status === "success") {
      const timer = setTimeout(() => {
        navigate("/calendar");
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [status, navigate]);

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
        className="bg-white rounded-xl shadow-lg border border-slate-200 w-full max-w-md p-8"
      >
        <div className="flex flex-col items-center text-center">
          {/* Loading State */}
          {status === "loading" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center"
            >
              <div
                className="h-16 w-16 rounded-full flex items-center justify-center mb-5"
                style={{ backgroundColor: "rgba(15, 81, 50, 0.1)" }}
              >
                <Loader2
                  className="h-8 w-8 animate-spin"
                  style={{ color: "var(--color-primary)" }}
                />
              </div>
              <h2 className="text-xl font-bold text-slate-800 mb-2">
                Connecting your calendar...
              </h2>
              <p className="text-sm text-slate-500">
                Please wait while we complete the authorization.
              </p>
            </motion.div>
          )}

          {/* Success State */}
          {status === "success" && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, type: "spring", stiffness: 200 }}
              className="flex flex-col items-center"
            >
              <div
                className="h-16 w-16 rounded-full flex items-center justify-center mb-5"
                style={{ backgroundColor: "rgba(15, 81, 50, 0.1)" }}
              >
                <Check className="h-8 w-8" style={{ color: "var(--color-primary)" }} />
              </div>
              <h2 className="text-xl font-bold text-slate-800 mb-2">
                Calendar Connected!
              </h2>
              <p className="text-sm text-slate-500">
                Redirecting to calendar...
              </p>
              <div className="mt-4 w-32 h-1 bg-slate-200 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 2, ease: "linear" }}
                  className="h-full rounded-full"
                  style={{ backgroundColor: "var(--color-primary)" }}
                />
              </div>
            </motion.div>
          )}

          {/* Error State */}
          {status === "error" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center"
            >
              <div className="h-16 w-16 rounded-full bg-red-50 flex items-center justify-center mb-5">
                <AlertCircle className="h-8 w-8 text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-slate-800 mb-2">
                Connection Failed
              </h2>
              <p className="text-sm text-slate-500 mb-6 max-w-sm">
                {errorMessage}
              </p>
              <button
                onClick={() => navigate("/calendar")}
                className="inline-flex items-center gap-2 px-6 py-3 text-white font-bold rounded-xl transition-colors text-sm"
                style={{ backgroundColor: "var(--color-primary)" }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "var(--color-primary)")
                }
              >
                Go back to Calendar
              </button>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default CalendarCallback;
